import {
  Autocomplete,
  Button,
  Checkbox,
  TextField,
  Box,
  Typography,
  CircularProgress,
  FormGroup,
  FormControlLabel,
} from '@mui/material'
import PageAppBar from 'components/PageAppBar'
import { base_url } from 'env'
import { useState, useEffect } from 'react'
import { useLoaderData, useNavigate } from 'react-router'
import { LicenseModel, ProfileModel, UserModel, PermissionsModel } from 'types'
import axios from 'axios'

const getDifference = (arr1: any[], arr2: any[], key: string) =>
  arr1.filter((a) => !arr2.some((b) => a[key] === b[key]))

export default function LicenseEdit() {
  const navigate = useNavigate()
  const initialData = useLoaderData() as LicenseModel

  const [isMedical, setIsMedical] = useState(initialData.isMedical)
  const [selectedInvited, setSelectedInvited] = useState<UserModel | null>(null)
  const [permissions, setPermissions] = useState<PermissionsModel>(
    initialData.permissions || {
      doActivities: false,
      editActivityHistory: false,
      consultActivityHistory: false,
      doTest: false,
      editTestHistory: false,
      consultTestHistory: false,
      reEnableTests: false,
      editPrizes: false,
      addPrizes: false,
      consultPrizes: false,
    }
  )

  const [allProfiles, setAllProfiles] = useState<ProfileModel[]>([])
  const [allUsers, setAllUsers] = useState<UserModel[]>([])

  const [initialLinkedProfiles, setInitialLinkedProfiles] = useState<
    ProfileModel[]
  >([])
  const [selectedProfiles, setSelectedProfiles] = useState<ProfileModel[]>([])

  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    const token = localStorage.getItem('token')
    if (!token) return

    setIsLoading(true)

    const fetchAllData = async () => {
      try {
        const [usersRes, profilesRes, linkedProfilesRes] = await Promise.all([
          axios.get(base_url + '/user/', {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(base_url + '/profile/', {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(base_url + `/license/${initialData.licenseKey}/profile`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ])

        setAllUsers(usersRes.data)
        setAllProfiles(profilesRes.data)

        setInitialLinkedProfiles(linkedProfilesRes.data)
        setSelectedProfiles(linkedProfilesRes.data)

        const invited = usersRes.data.find(
          (u: UserModel) => u.login === initialData.invitedUserLogin
        )
        setSelectedInvited(invited || null)
      } catch (error) {
        console.error('Failed to load license data', error)
        navigate('/licenses')
      } finally {
        setIsLoading(false)
      }
    }

    fetchAllData()
  }, [initialData.licenseKey, initialData.invitedUserLogin, navigate])

  const handleSave = async () => {
    const token = localStorage.getItem('token')
    if (!token) return

    setIsSaving(true)

    const profilesToAdd = getDifference(
      selectedProfiles,
      initialLinkedProfiles,
      'id'
    )
    const profilesToRemove = getDifference(
      initialLinkedProfiles,
      selectedProfiles,
      'id'
    )

    const apiCalls = []

    const licenseUpdatePayload = {
      isMedical: isMedical,
      invitedUserLogin: selectedInvited?.login || null,
      permissions: permissions,
    }
    apiCalls.push(
      axios.put(
        `${base_url}/license/${initialData.licenseKey}`,
        licenseUpdatePayload,
        { headers: { Authorization: `Bearer ${token}` } }
      )
    )

    profilesToAdd.forEach((p) => {
      apiCalls.push(
        axios.post(
          `${base_url}/license/${initialData.licenseKey}/profile`,
          { profileId: p.id },
          { headers: { Authorization: `Bearer ${token}` } }
        )
      )
    })

    profilesToRemove.forEach((p) => {
      apiCalls.push(
        axios.delete(
          `${base_url}/license/${initialData.licenseKey}/profile/${p.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        )
      )
    })

    try {
      await Promise.all(apiCalls)
      alert('Licence mise à jour avec succès !')
      navigate(`/license/${initialData.licenseKey}`)
    } catch (error) {
      console.error('Failed to update license:', error)
      alert('Une erreur est survenue lors de la mise à jour.')
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <PageAppBar pageName="Chargement...">
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </PageAppBar>
    )
  }

  return (
    <PageAppBar
      pageName={`Modifier Licence: ${initialData.licenseKey.substring(0, 8)}...`}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
          alignItems: 'center',
          padding: '2rem',
          maxWidth: '700px',
          margin: '0 auto',
        }}
      >
        <Typography variant="h5">Informations Générales</Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <Typography>Licence Médicale :</Typography>
          <Checkbox
            checked={isMedical}
            onChange={(e) => setIsMedical(e.target.checked)}
          />
        </Box>

        <Autocomplete
          fullWidth
          options={allUsers}
          getOptionLabel={(option) => option.login}
          value={selectedInvited}
          onChange={(event, newValue) => setSelectedInvited(newValue)}
          renderInput={(params) => (
            <TextField {...params} label="Compte invité (Possesseur)" />
          )}
        />

        <Typography variant="h5" sx={{ mt: 2 }}>
          Profils Associés
        </Typography>
        <Autocomplete
          multiple
          fullWidth
          id="profiles-autocomplete"
          options={allProfiles}
          getOptionLabel={(option) =>
            `${option.firstName} ${option.lastName || ''} (${option.createdBy})`
          }
          value={selectedProfiles}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(event, newValue) => setSelectedProfiles(newValue)}
          renderInput={(params) => <TextField {...params} label="Profils" />}
        />

        <Typography variant="h5" sx={{ mt: 2 }}>
          Permissions
        </Typography>

        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={permissions.doActivities}
                onChange={(e) =>
                  setPermissions({
                    ...permissions,
                    doActivities: e.target.checked,
                  })
                }
              />
            }
            label="Faire des activités"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={permissions.consultActivityHistory}
                onChange={(e) =>
                  setPermissions({
                    ...permissions,
                    consultActivityHistory: e.target.checked,
                  })
                }
              />
            }
            label="Consulter l'historique d'activités"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={permissions.editActivityHistory}
                onChange={(e) =>
                  setPermissions({
                    ...permissions,
                    editActivityHistory: e.target.checked,
                  })
                }
              />
            }
            label="Modifier l'historique d'activités"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={permissions.doTest}
                onChange={(e) =>
                  setPermissions({ ...permissions, doTest: e.target.checked })
                }
              />
            }
            label="Faire des tests"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={permissions.consultTestHistory}
                onChange={(e) =>
                  setPermissions({
                    ...permissions,
                    consultTestHistory: e.target.checked,
                  })
                }
              />
            }
            label="Consulter l'historique des tests"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={permissions.editTestHistory}
                onChange={(e) =>
                  setPermissions({
                    ...permissions,
                    editTestHistory: e.target.checked,
                  })
                }
              />
            }
            label="Modifier l'historique des tests"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={permissions.reEnableTests}
                onChange={(e) =>
                  setPermissions({
                    ...permissions,
                    reEnableTests: e.target.checked,
                  })
                }
              />
            }
            label="Activer/Désactiver les tests"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={permissions.addPrizes}
                onChange={(e) =>
                  setPermissions({
                    ...permissions,
                    addPrizes: e.target.checked,
                  })
                }
              />
            }
            label="Ajouter des récompenses"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={permissions.consultPrizes}
                onChange={(e) =>
                  setPermissions({
                    ...permissions,
                    consultPrizes: e.target.checked,
                  })
                }
              />
            }
            label="Consulter les récompenses"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={permissions.editPrizes}
                onChange={(e) =>
                  setPermissions({
                    ...permissions,
                    editPrizes: e.target.checked,
                  })
                }
              />
            }
            label="Modifier les récompenses"
          />
        </FormGroup>

        <Button
          variant="contained"
          color="success"
          onClick={handleSave}
          disabled={isSaving}
          sx={{ mt: 3, minWidth: 150 }}
        >
          {isSaving ? <CircularProgress size={24} /> : 'Sauvegarder'}
        </Button>
      </Box>
    </PageAppBar>
  )
}
